/* ========================================================================
   ヘッダードロワー アコーディオン制御
   モバイルドロワー内の階層メニュー開閉とリセットを管理する。
   ======================================================================== */
(() => {
  const HEADER_DRAWER_SELECTOR = 'header-drawer';
  const ACCORDION_ITEM_SELECTOR = '[data-aureme-accordion]';
  const ACCORDION_TOGGLE_SELECTORS = ['.aureme-menu-item__header', '.aureme-menu-subitem--toggle'];
  const TOGGLE_SELECTOR = ACCORDION_TOGGLE_SELECTORS.map(
    (selector) => `${ACCORDION_ITEM_SELECTOR} > ${selector}`,
  ).join(', ');
  const DIRECT_TOGGLE_SELECTOR = ACCORDION_TOGGLE_SELECTORS.map(
    (selector) => `:scope > ${selector}`,
  ).join(', ');
  const DRAWER_DETAILS_ID = 'Details-menu-drawer-container';
  const OPEN_CLASS = 'aureme-menu-item--open';

  class HeaderDrawerAccordionController {
    constructor(root = document) {
      this.root = root;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDetailsToggle = this.handleDetailsToggle.bind(this);
    }

    connect() {
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('keydown', this.handleKeydown);
      this.root.addEventListener('toggle', this.handleDetailsToggle, true);
    }

    getAccordionToggle(target) {
      if (!(target instanceof Element)) return null;
      return target.closest(TOGGLE_SELECTOR);
    }

    getAccordionCollapse(item) {
      return item?.querySelector(':scope > .aureme-menu-item__collapse') || null;
    }

    /**
     * アコーディオンの展開状態を切り替える。
     * scrollHeight を使っており、子リンク数に応じた高さへ自然に追従する。
     * @param {HTMLElement} toggle - 操作対象のトグル
     */
    toggleAccordion(toggle) {
      const item = toggle.closest(ACCORDION_ITEM_SELECTOR);
      const collapse = this.getAccordionCollapse(item);
      if (!item || !collapse) return;

      const isOpen = item.classList.contains(OPEN_CLASS);

      if (isOpen) {
        /* 閉じる時は現在の高さから 0 に落とし、リフローを挟んで transition を確実に発火させる */
        collapse.style.maxHeight = `${collapse.scrollHeight}px`;
        void collapse.offsetHeight;
        collapse.style.maxHeight = '0';
        item.classList.remove(OPEN_CLASS);
        toggle.setAttribute('aria-expanded', 'false');
        return;
      }

      item.classList.add(OPEN_CLASS);
      toggle.setAttribute('aria-expanded', 'true');
      collapse.style.maxHeight = `${collapse.scrollHeight}px`;

      /* 展開後は max-height を外し、孫アコーディオンの開閉でも高さが追従するようにする */
      collapse.addEventListener(
        'transitionend',
        () => {
          if (item.classList.contains(OPEN_CLASS)) {
            collapse.style.maxHeight = 'none';
          }
        },
        { once: true },
      );
    }

    /**
     * ドロワーを閉じたタイミングですべてのアコーディオン状態を初期化する。
     * @param {ParentNode} root - リセット対象の親要素
     */
    resetAllAccordions(root) {
      for (const item of root.querySelectorAll(`.${OPEN_CLASS}`)) {
        item.classList.remove(OPEN_CLASS);

        const collapse = this.getAccordionCollapse(item);
        if (collapse) {
          collapse.style.maxHeight = '0';
        }

        const header = item.querySelector(DIRECT_TOGGLE_SELECTOR);
        if (header) {
          header.setAttribute('aria-expanded', 'false');
        }
      }
    }

    handleClick(event) {
      const toggle = this.getAccordionToggle(event.target);
      if (!toggle?.closest(HEADER_DRAWER_SELECTOR)) return;

      this.toggleAccordion(toggle);
    }

    handleKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const toggle = this.getAccordionToggle(event.target);
      if (!toggle?.closest(HEADER_DRAWER_SELECTOR)) return;

      event.preventDefault();
      toggle.click();
    }

    handleDetailsToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.id !== DRAWER_DETAILS_ID || details.open) return;

      this.resetAllAccordions(details);
    }
  }

  if (!window.AuremeHeaderDrawerAccordionInitialized) {
    window.AuremeHeaderDrawerAccordionInitialized = true;
    new HeaderDrawerAccordionController(document).connect();
  }
})();
